import React, { useState, useEffect } from 'react';
import { Target, CheckCircle, XCircle } from 'lucide-react';
import apiClient from '../services/apiClient';
import { useAuth } from '../context/AuthContext';

function Scoreboard() {
  const [ranking, setRanking] = useState([]);
  const [userRanking, setUserRanking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPredictions, setUserPredictions] = useState([]);
  const [loadingPredictions, setLoadingPredictions] = useState(false);
  
  const { user } = useAuth();

  useEffect(() => {
    const fetchRanking = async () => {
      try {
        setLoading(true);
        // Fetch user's ranking
        const userRankRes = await apiClient.get('/scores/ranking/me');
        setUserRanking(userRankRes.data);

        // Fetch full ranking
        const rankingRes = await apiClient.get('/scores/ranking');
        setRanking(rankingRes.data.ranking);

        setError(null);
      } catch (err) {
        setError('Erro ao carregar ranking');
        console.error('Error fetching ranking:', err);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchRanking();
    }
  }, [user]);

  const openUserModal = async (u) => {
    setSelectedUser(u);
    setLoadingPredictions(true);
    setUserPredictions([]); // Limpa palpites anteriores para evitar piscar o do usuário antigo
    try {
      const res = await apiClient.get(`/predictions/user/${u.id}`);
      setUserPredictions(res.data);
    } catch (err) {
      console.error('Error fetching user predictions:', err);
    } finally {
      setLoadingPredictions(false);
    }
  };

  if (!user) {
    return <div className="container">Faça login para ver o ranking</div>;
  }

  if (loading) {
    return <div className="container">Carregando ranking...</div>;
  }

  if (error) {
    return <div className="container alert alert-error">{error}</div>;
  }

  return (
    <div className="container">
      <h1 className="text-2xl font-bold mb-6 text-center text-primary">Ranking</h1>

      {userRanking && (
        <div className="card mb-6">
          <h3 className="text-xl font-bold mb-2">Sua Posição</h3>
          <div className="text-center py-4">
            <div className="text-4xl font-bold text-secondary-dark">{userRanking.rank ? `${userRanking.rank}º` : '-'}</div>
            <div className="text-lg">{userRanking.score} pontos</div>
            <div className="text-sm text-gray-600">de {userRanking.totalUsers} participantes verificados</div>
          </div>
        </div>
      )}

      <div className="card">
        <h3 className="text-xl font-bold mb-4">Ranking Geral</h3>
        {ranking.length === 0 ? (
          <div className="text-center py-4">Nenhum usuário participando no momento</div>
        ) : (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>Posição</th>
                  <th>Usuário</th>
                  <th>Pontos</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  let currentRank = 1;
                  let prevScore = null;
                  return ranking.map((entry) => {
                    const isVerified = entry.user.payment_verified;
                    let displayRank = '-';

                    if (isVerified) {
                      if (prevScore !== null && entry.score < prevScore) {
                        currentRank++;
                      }
                      displayRank = `${currentRank}º`;
                      prevScore = entry.score;
                    }

                    return (
                      <tr
                        key={entry.user.id}
                        className={`cursor-pointer hover:bg-gray-50 transition-colors ${entry.user.id === user.id ? 'bg-yellow-50 hover:bg-yellow-100' : ''} ${!isVerified ? 'opacity-60' : ''}`}
                        onClick={() => openUserModal(entry.user)}
                      >
                        <td className="font-bold text-gray-700">{displayRank}</td>
                        <td>
                          <div className="flex items-center gap-2">
                            <span className={!isVerified ? 'text-gray-500' : ''}>{entry.user.name}</span>
                            {!isVerified && (
                              <span className="text-[10px] bg-red-100 text-red-800 px-1.5 py-0.5 rounded font-bold uppercase tracking-wider">Não Verificado</span>
                            )}
                          </div>
                          {entry.user.nickname && <div className="text-sm text-gray-600">@{entry.user.nickname}</div>}
                          {user.id === entry.user.id && <span className="admin-badge mt-1 inline-block">Você</span>}
                        </td>
                        <td className="font-bold text-lg">{entry.score}</td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de Palpites do Usuário */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 animate-fade-in motion-reduce:animate-none">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col animate-scale-in motion-reduce:animate-none">
            <div className="p-4 border-b flex justify-between items-center bg-gray-50 rounded-t-lg">
              <h3 className="text-xl font-bold text-blue-800">
                Palpites Finalizados de {selectedUser.name}
              </h3>
              <button 
                onClick={() => setSelectedUser(null)}
                className="text-gray-500 hover:text-gray-700 font-bold text-2xl leading-none"
              >
                &times;
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto flex-1">
              {loadingPredictions ? (
                <div className="text-center py-8 text-gray-600">Carregando palpites...</div>
              ) : userPredictions.length === 0 ? (
                <div className="text-center py-8 text-gray-600">Nenhum palpite para partidas finalizadas.</div>
              ) : (
                <div className="space-y-4">
                  {userPredictions.map(pred => {
                    const match = pred.match;
                    const uH = pred.home_score;
                    const uA = pred.away_score;
                    const rH = match.home_score;
                    const rA = match.away_score;

                    const isKnockoutMatch = match.round && match.round !== 'group_stage';
                    const matchIsKnockoutDraw = isKnockoutMatch && rH === rA && match.winner_on_draw;
                    const isExact = uH === rH && uA === rA;
                    const userOutcome = uH > uA ? 'home' : uH < uA ? 'away' : 'draw';
                    const realOutcome = rH > rA ? 'home' : rH < rA ? 'away' : 'draw';
                    const isOutcomeCorrect = userOutcome === realOutcome;
                    const correctWinner = pred.winner_on_draw === match.winner_on_draw;

                    // Backend computes points using current scoring rules (single source of truth)
                    const pts = pred.points ?? 0;

                    let badgeBg = 'bg-red-100 text-red-800';
                    let BadgeIcon = XCircle;
                    let badgeText = 'Errou';

                    if (matchIsKnockoutDraw) {
                      if (isExact && correctWinner) {
                        badgeBg = 'bg-green-100 text-primary';
                        BadgeIcon = Target;
                        badgeText = 'Placar Exato + Classificado';
                      } else if (isExact && !correctWinner) {
                        badgeBg = 'bg-green-100 text-primary';
                        BadgeIcon = Target;
                        badgeText = 'Placar Exato (errou quem avança)';
                      } else if (userOutcome === 'draw' && correctWinner) {
                        badgeBg = 'bg-blue-100 text-blue-800';
                        BadgeIcon = CheckCircle;
                        badgeText = 'Empate + Classificado';
                      } else if (userOutcome === 'draw') {
                        badgeBg = 'bg-blue-100 text-blue-800';
                        BadgeIcon = CheckCircle;
                        badgeText = 'Acertou Empate';
                      }
                    } else if (isExact) {
                      badgeBg = 'bg-green-100 text-primary';
                      BadgeIcon = Target;
                      badgeText = 'Placar Exato';
                    } else if (isOutcomeCorrect) {
                      badgeBg = 'bg-blue-100 text-blue-800';
                      BadgeIcon = CheckCircle;
                      badgeText = `Acertou ${realOutcome === 'draw' ? 'Empate' : 'Vencedor'}`;
                    }

                    return (
                      <div key={pred.id} className="border rounded-lg p-4 bg-white shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="text-sm text-gray-500 mb-2">
                            {new Date(match.kickoff_time).toLocaleString('pt-BR', {
                              day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
                            })}
                          </div>
                          
                          <div className="flex flex-col gap-2">
                            {/* Real Score */}
                            <div className="flex justify-between items-center bg-gray-50 p-2 rounded gap-1">
                              <span className="font-semibold text-right flex-1 min-w-0 truncate">{match.home_team_pt || match.home_team}</span>
                              <span className="px-2 sm:px-4 font-bold text-lg text-gray-800 shrink-0 text-center">
                                {rH} x {rA}
                              </span>
                              <span className="font-semibold text-left flex-1 min-w-0 truncate">{match.away_team_pt || match.away_team}</span>
                            </div>
                            {match.winner_on_draw && (
                              <div className="text-xs text-orange-600 font-medium text-center">
                                {match.winner_on_draw === 'home'
                                  ? (match.home_team_pt || match.home_team)
                                  : (match.away_team_pt || match.away_team)} avança nos pênaltis
                              </div>
                            )}

                            {/* User Prediction */}
                            <div className="flex justify-between items-center px-2 py-1 gap-1">
                              <span className="text-sm text-gray-500 text-right flex-1 min-w-0">Palpite:</span>
                              <span className="font-bold bg-blue-50 text-blue-800 px-2 sm:px-3 py-1 rounded border border-blue-100 shrink-0 text-center">
                                {uH} x {uA}
                              </span>
                              <span className="flex-1 min-w-0"></span>
                            </div>
                            {pred.winner_on_draw && (
                              <div className="text-xs text-orange-600 font-medium text-center">
                                Avança: {pred.winner_on_draw === 'home'
                                  ? (match.home_team_pt || match.home_team)
                                  : (match.away_team_pt || match.away_team)}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex flex-row md:flex-col justify-center items-center gap-2 w-full md:w-auto md:min-w-[140px] mt-2 md:mt-0 pt-3 md:pt-0 border-t md:border-t-0 md:border-l border-gray-100 pl-0 md:pl-4">
                          <div className={`inline-flex items-center justify-center gap-1 px-3 py-1.5 ${badgeBg} text-xs font-bold rounded-full w-full`}>
                            <BadgeIcon className="w-3.5 h-3.5" aria-label={badgeText} /> {badgeText}
                          </div>
                          <div className="inline-flex items-center justify-center gap-1 px-3 py-1.5 bg-yellow-100 text-yellow-800 text-xs font-bold rounded-full w-full">
                            +{pts} Ponto{pts !== 1 ? 's' : ''}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            <div className="p-4 border-t bg-gray-50 rounded-b-lg flex justify-end">
              <button 
                onClick={() => setSelectedUser(null)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 font-semibold transition-colors focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 focus:outline-none"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Scoreboard;